import { z } from 'zod';
import { nullableList } from '../../zod-helpers';

const uuid = z.string().uuid();
const isoCurrency = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/u, { message: 'Use a 3-letter ISO 4217 code.' });

/**
 * Order state machine — lifted verbatim from
 * docs/api/admin/commerce/orders/list.md. The transitions exposed by the
 * admin API are: paid → fulfilling, paid|fulfilling → shipped, * → refunded
 * or partially_refunded. `created` and `delivered` come from upstream events
 * (checkout / carrier webhook) and aren't admin-driven.
 */
export const orderStateSchema = z.enum([
  'created',
  'paid',
  'fulfilling',
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
  'partially_refunded',
]);
export type OrderState = z.infer<typeof orderStateSchema>;

export const orderListItemSchema = z.object({
  id: uuid,
  short_code: z.string(),
  user_id: uuid.nullable().optional(),
  total_cents: z.number().int().nonnegative(),
  currency: isoCurrency,
  state: orderStateSchema,
  placed_at: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});
export type OrderListItem = z.infer<typeof orderListItemSchema>;
export const orderListSchema = nullableList(orderListItemSchema);

export const orderListFiltersSchema = z.object({
  state: orderStateSchema.optional(),
  designer: uuid.optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type OrderListFilters = z.infer<typeof orderListFiltersSchema>;

export const orderShipBodySchema = z.object({
  carrier: z.string().min(1).max(100),
  tracking_number: z.string().min(1).max(100),
});
export type OrderShipBody = z.infer<typeof orderShipBodySchema>;

export const orderRefundBodySchema = z
  .object({
    amount_cents: z.number().int().min(1).optional(),
    reason: z.string().max(500).optional(),
  })
  .refine(
    // amount_cents is optional but, when present, must be at least 1.
    () => true,
    { message: 'noop' }
  );
export type OrderRefundBody = z.infer<typeof orderRefundBodySchema>;

/**
 * Optional admin order detail (no `GET /admin/v1/orders/{id}` documented yet —
 * see Phase 4 §7 Open Questions). Schema is permissive so MSW or a future
 * real endpoint can flesh it out without breaking callers.
 */
const orderItemSchema = z.object({
  id: uuid,
  product_id: uuid.nullable().optional(),
  variant_id: uuid.nullable().optional(),
  product_name: z.string().nullable().optional(),
  variant_label: z.string().nullable().optional(),
  designer_name: z.string().nullable().optional(),
  quantity: z.number().int().positive(),
  unit_price_cents: z.number().int().nonnegative(),
  line_total_cents: z.number().int().nonnegative(),
});

const orderShipmentSchema = z.object({
  id: uuid,
  carrier: z.string().nullable().optional(),
  tracking_number: z.string().nullable().optional(),
  state: z.string(),
  shipped_at: z.string().nullable().optional(),
});

export const orderDetailSchema = orderListItemSchema.extend({
  items: z.array(orderItemSchema).optional(),
  shipments: z.array(orderShipmentSchema).optional(),
  subtotal_cents: z.number().int().nonnegative().optional(),
  shipping_cents: z.number().int().nonnegative().optional(),
  tax_cents: z.number().int().nonnegative().optional(),
});
export type OrderDetail = z.infer<typeof orderDetailSchema>;

/**
 * Customer reveal payload. The orders list omits PII; per
 * docs/api/admin/commerce/orders/list.md the lookup goes through admin support
 * tooling (`/v1/orders/{short_code}` w/ admin auth) — exact endpoint pending
 * confirmation (Phase 4 §7 Q2). The schema is intentionally narrow: only the
 * fields a commerce ops manager needs to contact the customer.
 */
export const orderCustomerRevealSchema = z.object({
  email: z.string().email().nullable().optional(),
  shipping_address: z
    .object({
      name: z.string().nullable().optional(),
      line1: z.string().nullable().optional(),
      line2: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      region: z.string().nullable().optional(),
      postal_code: z.string().nullable().optional(),
      country: z.string().length(2).nullable().optional(),
      phone: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type OrderCustomerReveal = z.infer<typeof orderCustomerRevealSchema>;
